/**
 * Brewlab (Sunderland, England) — the British regional ale strains sold as
 * agar slopes.
 *
 * Brewlab is a brewing training and analysis house rather than a yeast lab in
 * the Wyeast mould, and its yeast catalogue is a collection of single-culture
 * isolates from British brewing regions — the Thames Valley, Kent and Sussex
 * in the south; Burton and the East Midlands; Devon; Yorkshire, Lancashire and
 * Tyneside; the Scottish Borders. Each is sold as a slope, a slant of agar with
 * the culture growing on its surface, which is why every entry here is `liquid`
 * and none carries a dosage: what arrives is a culture to grow up in a starter,
 * not a pitch.
 *
 * What Brewlab publish, and where it came from. Twelve shop pages under
 * brewlab.co.uk/product-category/home-brew-yeast-slopes/ carry a summary
 * paragraph each, and every one links a maker-hosted "Yeast Profile" PDF at
 * brewlab.co.uk/wp-content/uploads/. Those PDFs were fetched and read in
 * September 2026, and they are a genuinely unusual document: cell and colony
 * morphology, growth curves in 1040 and 1080 wort with a measured delay to
 * fermentation start, a flocculation and sedimentation verdict, a POF test
 * result, hydrogen sulphide potential under stress, acid and oxygen stress
 * resistance, and a twenty-spoke flavour radar. The strain codes — Thames
 * Valley 1, Sussex 1, Burton 1, Borders 1 — are Brewlab's own, from their
 * strain list at brewlab.co.uk/services/yeast-list/.
 *
 * What they do not publish is the pair of numbers a recipe calculator wants.
 * There is no attenuation figure anywhere in the range, and no alcohol
 * tolerance, so **every entry in this file is `representative`** and says so.
 * The attenuation bands follow one rule, applied without exception rather than
 * tuned per strain: a strain Brewlab call low-flocculating is entered 72–78 %,
 * a medium one 70–76 %, a high one 68–75 %, on the ordinary observation that a
 * culture which drops early finishes higher. `alcoholTolerancePercent` is
 * `null` throughout, and `sta1` is too — the POF test Brewlab run detects
 * phenolic off-flavour, not the STA1 glucoamylase, and the two say nothing
 * about each other.
 *
 * Temperature is Brewlab's own 15–23 °C, from the instruction sheet at
 * brewlab.co.uk/wp-content/uploads/YEAST-INSTRUCTION-2021-P-V1.pdf that every
 * product page links. It is published for the range as a whole rather than per
 * strain, which is the second reason these entries are not `spec-sheet`; it is
 * still Brewlab's figure and not ours, so it is the same band on all twelve.
 *
 * Flocculation *is* per strain and is theirs. Brewlab report it separately from
 * sedimentation, and the two disagree often enough to be worth reading: Kent
 * and Sussex flocculate hard but settle slowly, while Burton and East Midlands
 * flocculate poorly and then drop a dense cake. The `flocculation` field is
 * their flocculation verdict, and the description says where sedimentation
 * pulls the other way.
 *
 * The shop pages route ordering through a retailer, so the links here are the
 * Brewlab product pages themselves — which is what they are, description, spec
 * sheet and all. Kent is sold under a numeric slug (`/product/5407/`) rather
 * than a named one, and is absent from the strain list page while present in
 * the shop; the figures for it come from its own profile PDF like the rest.
 */

import type { Yeast } from '../../schema/yeast';

/** Brewlab's British regional slope cultures. @see Yeast */
export const brewlabYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // London and the south east
  // -------------------------------------------------------------------------
  {
    id: 'brewlab-thames-valley-1',
    lab: 'Brewlab',
    productCode: 'Thames Valley 1',
    name: 'Thames Valley 1',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The all-rounder of the collection and the one to reach for first: moderate speed, moderate esters, and no strong opinion about the water it is given. Brewlab test it POF-negative with only light sulfur under stress, so it finishes clean, throws a good head and then drops a compact cake. Attenuation is our class-typical band — Brewlab publish none — and the temperature is their range-wide 15–23 °C.',
    tags: ['british', 'english-ale', 'bitter', 'thames-valley', 'clean', 'all-rounder', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-thames-valley-1-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-thames-valley-2',
    lab: 'Brewlab',
    productCode: 'Thames Valley 2',
    name: 'Thames Valley 2',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "A slow, steady southern English strain that wants a mineral wort and repays it with a low-ester, POF-negative beer — Brewlab put it at bitters and stronger ales alike. It flocculates moderately but settles slowly into a broad, loose bed, so give the cask or the conditioning tank time. Attenuation here is class-typical rather than published, and 15–23 °C is Brewlab's figure for the whole range.",
    tags: ['british', 'english-ale', 'bitter', 'strong-ale', 'thames-valley', 'clean', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-thames-valley-2-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-thames-valley-3',
    lab: 'Brewlab',
    productCode: 'Thames Valley 3',
    name: 'Thames Valley 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A London ale culture, slow to build and the most flocculent of the Thames Valley three: a light head, then a hard, rapid drop to a firm sediment that makes it easy to crop and to fine. Low to moderate esters, very little sulfur, POF-negative. Brewlab publish no attenuation, so the band is class-typical for a strain that clears this early, and the 15–23 °C is theirs.',
    tags: ['british', 'english-ale', 'london', 'bitter', 'porter', 'flocculent', 'clean', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-thames-valley-3-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-sussex-1',
    lab: 'Brewlab',
    productCode: 'Sussex 1',
    name: 'Sussex',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "The expressive one in the southern group: a fast fermenter on a mineral wort that pushes strong esters, and pushes them harder as gravity rises — Brewlab flag light phenolics and a spicy edge alongside moderate sulfur. Strong flocculation and a big head, but slow sedimentation into a broad bed. Attenuation is our class-typical band, temperature Brewlab's range-wide 15–23 °C.",
    tags: ['british', 'english-ale', 'sussex', 'bitter', 'strong-ale', 'estery', 'spicy', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-sussex-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-kent',
    lab: 'Brewlab',
    productCode: 'Kent',
    name: 'Kent',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "The quickest starter Brewlab measure — four hours to fermentation in 1040 wort — on a high-mineral bitter wort, and the most sulfurous, with moderate to strong esters over it and a moderate POF result behind the light phenolics. It flocculates high and heads well but sediments slowly, so it hangs about. Attenuation is class-typical rather than published; 15–23 °C is Brewlab's.",
    tags: ['british', 'english-ale', 'kent', 'bitter', 'estery', 'sulphur', 'fast', 'slope'],
    url: 'https://brewlab.co.uk/product/5407/',
    provenance: 'representative',
  },
  // -------------------------------------------------------------------------
  // The Midlands
  // -------------------------------------------------------------------------
  {
    id: 'brewlab-east-midlands-1',
    lab: 'Brewlab',
    productCode: 'East Midlands 1',
    name: 'East Midlands',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A low-mineral Midlands strain for bitters and dark beers, low in esters and POF-negative, with moderate sulfur only when stressed. Brewlab call its flocculation poor and its sedimentation strong — it stays up working, then drops a firm bed at the end. That late-clearing habit is why the class-typical attenuation band here sits at the high end; Brewlab publish no figure, and 15–23 °C is theirs.',
    tags: ['british', 'english-ale', 'midlands', 'bitter', 'dark-ale', 'mild', 'clean', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-east-midlands-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-burton-1',
    lab: 'Brewlab',
    productCode: 'Burton 1',
    name: 'Burton upon Trent',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The classic Burton character: steady rather than quick, and the strongest sulfur potential in the collection, which is the Burton snatch brewers either want or brew around. Light phenolics too. It flocculates slowly with a thin head, then sediments hard into a dense cake. Brewlab publish no attenuation, so the band is class-typical for a late-dropping strain; the temperature is their range-wide 15–23 °C.',
    tags: ['british', 'english-ale', 'burton', 'bitter', 'ipa', 'sulphur', 'pale-ale', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-yeast-slope-burton-upon-trent/',
    provenance: 'representative',
  },
  // -------------------------------------------------------------------------
  // The West Country
  // -------------------------------------------------------------------------
  {
    id: 'brewlab-devon-1',
    lab: 'Brewlab',
    productCode: 'Devon 1',
    name: 'Devon',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A West Country strain that starts fast on a soft, low-mineral wort and makes the most obviously fruity beer of the twelve — Brewlab name banana in the ester profile, over moderate to strong sulfur and light phenolics. Big head, moderate flocculation, moderate settling. The attenuation band is class-typical because Brewlab publish none; the 15–23 °C is their figure for the range.',
    tags: [
      'british',
      'english-ale',
      'devon',
      'west-country',
      'bitter',
      'estery',
      'banana',
      'slope',
    ],
    url: 'https://brewlab.co.uk/product/brewlab-devon-yeast-slope/',
    provenance: 'representative',
  },
  // -------------------------------------------------------------------------
  // Northern England
  // -------------------------------------------------------------------------
  {
    id: 'brewlab-yorkshire',
    lab: 'Brewlab',
    productCode: 'Yorkshire',
    name: 'Yorkshire',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "The Yorkshire square strain, and it behaves like one: a high-mineral wort, a good rocky head, moderate esters and enough sulfur to notice. It returns the strongest POF result Brewlab record, though their own tasting note calls the phenolics light. Flocculation and sedimentation are both middling. Attenuation is class-typical, not published; the 15–23 °C band is Brewlab's own.",
    tags: [
      'british',
      'english-ale',
      'yorkshire',
      'bitter',
      'estery',
      'sulphur',
      'phenolic',
      'slope',
    ],
    url: 'https://brewlab.co.uk/product/brewlab-yorkshire-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-lancashire',
    lab: 'Brewlab',
    productCode: 'Lancashire',
    name: 'Lancashire',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The quiet north-western strain: slow but dependable on a high-mineral wort, low in esters, low in sulfur, no phenolics to speak of. Brewlab measure one of the longest lags in the collection, six hours in 1040 wort, so pitch a healthy starter. Moderate flocculation and good settling. Attenuation is a class-typical band because Brewlab publish none, and 15–23 °C is their range-wide figure.',
    tags: ['british', 'english-ale', 'lancashire', 'bitter', 'mild', 'clean', 'low-ester', 'slope'],
    url: 'https://brewlab.co.uk/product/brewlab-lancashire-yeast-slope/',
    provenance: 'representative',
  },
  {
    id: 'brewlab-tyneside',
    lab: 'Brewlab',
    productCode: 'Tyneside',
    name: 'Tyneside',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "A north-eastern strain from Brewlab's own doorstep, and the most restrained of the set: soft water, light esters, low sulfur, no phenolic character. Flocculation is low, though it settles well once it is done, and it is the one strain Brewlab rate only medium for acid resistance — worth knowing before acid washing. Attenuation is class-typical rather than published; 15–23 °C is theirs.",
    tags: [
      'british',
      'english-ale',
      'tyneside',
      'north-east',
      'bitter',
      'clean',
      'neutral',
      'slope',
    ],
    url: 'https://brewlab.co.uk/product/brewlab-tyneside-yeast-slope/',
    provenance: 'representative',
  },
  // -------------------------------------------------------------------------
  // Scotland
  // -------------------------------------------------------------------------
  {
    id: 'brewlab-borders-1',
    lab: 'Brewlab',
    productCode: 'Borders 1',
    name: 'Scottish Borders',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Brewlab point this Borders strain at bitters, brown ales and dark Scottish beers on a soft wort, where its strong esters have malt to sit against. Their tasting note claims phenolic character while their own POF test comes back negative, so read that expectation carefully. The slowest starter they measure — six hours in 1040 wort, nine in 1080 — then settles well. Attenuation is our class-typical band; 15–23 °C is Brewlab's range-wide figure.",
    tags: [
      'british',
      'scottish-ale',
      'borders',
      'bitter',
      'brown-ale',
      'dark-ale',
      'estery',
      'slope',
    ],
    url: 'https://brewlab.co.uk/product/brewlab-scottish-borders-yeast-slope/',
    provenance: 'representative',
  },
];
